// Rails Operations — Bank Admin tab (Payfinia IPX replacement).
// Five sections: status, volume, fraud controls, RFI/RFP, API gateway.

import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  CheckCircle2, AlertTriangle, XCircle, Loader2, RefreshCw,
  Trash2, Plus, Shield, Clock, Send, Webhook, Key, Activity, X,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid,
} from "recharts";
import { useBranding } from "../../BrandingContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v2/admin/rails`;

// ── Tiny helpers ──

const fmtUSD = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtRel = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const min = Math.round(diffMs / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

const StatusDot = ({ status }) => {
  const map = {
    operational: "bg-emerald-500",
    degraded: "bg-amber-500",
    down: "bg-rose-500",
    maintenance: "bg-slate-400",
  };
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${map[status] || "bg-slate-300"}`} />;
};

const Toast = ({ msg, kind = "success", onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 2400);
    return () => clearTimeout(t);
  }, [onClose]);
  if (!msg) return null;
  const bg = kind === "success" ? "bg-emerald-600" : "bg-rose-600";
  return (
    <div className={`fixed bottom-6 right-6 ${bg} text-white px-4 py-2.5 rounded-lg shadow-lg flex items-center gap-2 z-50 text-sm`} data-testid="toast">
      <CheckCircle2 className="w-4 h-4" />{msg}
    </div>
  );
};

// ── SECTION 1 — Status ──

const StatusSection = () => {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const r = await axios.get(`${API}/status`);
      setData(r.data);
    } catch (_e) { /* noop */ }
    setRefreshing(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="grid grid-cols-1 md:grid-cols-4 gap-4"><div className="col-span-4 h-32 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div></div>;

  const r = data.rails;
  const card = "bg-white rounded-xl border border-slate-200 p-4";
  const fmtLatency = (ms) => ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;

  return (
    <div data-testid="rails-status-cards">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* FedNow */}
        <div className={card} data-testid="rails-card-fednow">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold text-slate-900">{r.fednow.label}</h3>
            <StatusDot status={r.fednow.status} />
          </div>
          <p className="text-xs text-emerald-600 font-semibold capitalize">{r.fednow.status}</p>
          <div className="mt-3 pt-3 border-t border-slate-100 space-y-1 text-xs text-slate-500">
            <div className="flex justify-between"><span>Uptime 24h</span><span className="font-mono text-slate-700">{r.fednow.uptime_24h_pct}%</span></div>
            <div className="flex justify-between"><span>Current latency</span><span className="font-mono text-slate-700">{fmtLatency(r.fednow.latency_ms)}</span></div>
          </div>
          <p className="text-[10px] text-slate-400 mt-3">Last checked: {new Date(r.fednow.last_checked).toLocaleTimeString()}</p>
        </div>

        {/* RTP */}
        <div className={card} data-testid="rails-card-rtp">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold text-slate-900">{r.rtp.label}</h3>
            <StatusDot status={r.rtp.status} />
          </div>
          <p className="text-xs text-emerald-600 font-semibold capitalize">{r.rtp.status}</p>
          <div className="mt-3 pt-3 border-t border-slate-100 space-y-1 text-xs text-slate-500">
            <div className="flex justify-between"><span>Uptime 24h</span><span className="font-mono text-slate-700">{r.rtp.uptime_24h_pct}%</span></div>
            <div className="flex justify-between"><span>Current latency</span><span className="font-mono text-slate-700">{fmtLatency(r.rtp.latency_ms)}</span></div>
          </div>
          <p className="text-[10px] text-slate-400 mt-3">Last checked: {new Date(r.rtp.last_checked).toLocaleTimeString()}</p>
        </div>

        {/* Stablecoin per-chain */}
        <div className={card} data-testid="rails-card-stablecoin">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold text-slate-900">{r.stablecoin.label}</h3>
            <StatusDot status={r.stablecoin.status} />
          </div>
          <div className="space-y-1.5 mt-2">
            {r.stablecoin.chains.map((c) => (
              <div key={c.chain} className="flex items-center justify-between text-xs" data-testid={`rails-chain-${c.chain}`}>
                <span className="text-slate-700">{c.label}</span>
                <span className="flex items-center gap-1.5">
                  <span className="font-mono text-slate-400">{fmtLatency(c.latency_ms)}</span>
                  <StatusDot status={c.status} />
                </span>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-slate-400 mt-3">Last checked: {new Date(r.stablecoin.last_checked).toLocaleTimeString()}</p>
        </div>

        {/* Tokenized */}
        <div className={card} data-testid="rails-card-tokenized">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold text-slate-900">{r.tokenized_deposit.label}</h3>
            <StatusDot status={r.tokenized_deposit.status} />
          </div>
          <p className="text-xs text-emerald-600 font-semibold capitalize">{r.tokenized_deposit.status}</p>
          <div className="mt-3 pt-3 border-t border-slate-100 space-y-1 text-xs text-slate-500">
            <div className="flex justify-between"><span>Bank issuer</span><span className="text-slate-700 font-medium">{r.tokenized_deposit.bank_issuer}</span></div>
            <div className="flex justify-between"><span>Current latency</span><span className="font-mono text-slate-700">{fmtLatency(r.tokenized_deposit.latency_ms)}</span></div>
          </div>
          <p className="text-[10px] text-slate-400 mt-3">Last checked: {new Date(r.tokenized_deposit.last_checked).toLocaleTimeString()}</p>
        </div>
      </div>

      <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
        <span>Last Updated: {new Date(data.checked_at).toLocaleString()}</span>
        <button onClick={load} disabled={refreshing} data-testid="rails-status-refresh"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50 transition font-semibold text-slate-700">
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>
    </div>
  );
};

// ── SECTION 2 — Volume ──
// Canonical rail colors (used everywhere in the product)
const RAIL_COLORS = {
  fednow: "#14b8a6",             // teal-500
  rtp: "#3b82f6",                // blue-500
  stablecoin: "#a855f7",         // purple-500
  tokenized_deposit: "#10b981",  // emerald-500
};

const VolumeSection = () => {
  const [period, setPeriod] = useState("24h");
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    axios.get(`${API}/volume?period=${period}`).then((r) => setData(r.data));
  }, [period]);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="rails-volume-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Volume by Rail</h3>
          <p className="text-xs text-slate-500">Stacked transaction volume across all four rails</p>
        </div>
        <div className="flex bg-slate-100 rounded-lg p-1" data-testid="rails-volume-period-toggle">
          {["24h", "7d", "30d"].map((p) => (
            <button key={p} onClick={() => setPeriod(p)} data-testid={`rails-volume-period-${p}`}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition ${period === p ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
              {p}
            </button>
          ))}
        </div>
      </div>

      {!data ? (
        <div className="h-72 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : (
        <>
          <div className="h-72" data-testid="rails-volume-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" stroke="#94a3b8" fontSize={11} />
                <YAxis stroke="#94a3b8" fontSize={11} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => fmtUSD(v)} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {Object.entries(RAIL_COLORS).map(([k, color]) => (
                  <Area key={k} type="monotone" dataKey={k} stackId="1" stroke={color} fill={color} fillOpacity={0.6} name={k.replace("_", " ")} />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <table className="w-full mt-5 text-sm" data-testid="rails-volume-table">
            <thead>
              <tr className="text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
                <th className="text-left py-2">Rail</th>
                <th className="text-right py-2">{period} Volume</th>
                <th className="text-right py-2">Tx Count</th>
                <th className="text-right py-2">Avg Tx Size</th>
                <th className="text-right py-2">Fees Earned</th>
              </tr>
            </thead>
            <tbody>
              {data.table.map((row) => (
                <tr key={row.rail} className="border-b border-slate-100" data-testid={`rails-volume-row-${row.rail}`}>
                  <td className="py-2.5">
                    <span className="inline-flex items-center gap-2 font-medium text-slate-900">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: RAIL_COLORS[row.rail] }} />
                      {row.label}
                    </span>
                  </td>
                  <td className="text-right font-mono text-slate-900">{fmtUSD(row.volume)}</td>
                  <td className="text-right text-slate-700">{row.tx_count}</td>
                  <td className="text-right font-mono text-slate-700">{fmtUSD(row.avg_tx_size)}</td>
                  <td className="text-right font-mono text-emerald-600">{fmtUSD(row.fees_earned)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

// ── SECTION 3 — Fraud Controls ──

const FraudControlsSection = ({ toast }) => {
  const [policy, setPolicy] = useState(null);
  const [devices, setDevices] = useState([]);
  const [rules, setRules] = useState([]);
  const [editing, setEditing] = useState({});
  const [saving, setSaving] = useState(false);
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [newRule, setNewRule] = useState({
    name: "", attribute: "member_age_days", operator: "<", value: 30,
    limit_type: "single_tx_max", limit_amount: 500,
  });

  const loadAll = useCallback(async () => {
    const [p, d, g] = await Promise.all([
      axios.get(`${API}/policy`),
      axios.get(`${API}/devices`),
      axios.get(`${API}/group-rules`),
    ]);
    setPolicy(p.data);
    setEditing({
      vl: p.data.velocity_limits, co: p.data.cool_off, caps: p.data.rail_caps_daily,
    });
    setDevices(d.data.devices);
    setRules(g.data.rules);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const savePolicy = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/policy`, {
        velocity_limits: editing.vl,
        cool_off: editing.co,
        rail_caps_daily: editing.caps,
      });
      toast("Policy saved");
      await loadAll();
    } catch (e) {
      toast("Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const revokeDevice = async (id) => {
    await axios.delete(`${API}/devices/${id}`);
    toast("Device revoked");
    loadAll();
  };

  const addRule = async () => {
    try {
      await axios.post(`${API}/group-rules`, newRule);
      toast("Rule added");
      setShowRuleForm(false);
      setNewRule({ name: "", attribute: "member_age_days", operator: "<", value: 30,
        limit_type: "single_tx_max", limit_amount: 500 });
      loadAll();
    } catch (e) {
      toast(e.response?.data?.detail || "Failed", "error");
    }
  };

  const deleteRule = async (id) => {
    await axios.delete(`${API}/group-rules/${id}`);
    toast("Rule deleted");
    loadAll();
  };

  if (!policy) return <div className="bg-white rounded-xl border border-slate-200 p-6 h-32 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  const InputField = ({ label, value, onChange, prefix = "$" }) => (
    <label className="block">
      <span className="text-xs text-slate-500 mb-1 block">{label}</span>
      <div className="flex items-center gap-2 bg-slate-50 rounded-lg border border-slate-200 px-3 py-2">
        {prefix && <span className="text-slate-400 text-sm">{prefix}</span>}
        <input type="number" value={value} onChange={(e) => onChange(Number(e.target.value))}
          className="flex-1 bg-transparent outline-none text-sm font-mono text-slate-900" />
      </div>
    </label>
  );

  return (
    <div className="space-y-5" data-testid="rails-fraud-section">
      {/* 3a + 3e velocity + caps */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Velocity Limits & Rail Caps</h3>
            <p className="text-xs text-slate-500">Per-member limits and per-rail daily ceilings</p>
          </div>
          <button onClick={savePolicy} disabled={saving} data-testid="rails-policy-save-btn"
            className="px-4 py-2 bg-[#007377] text-white text-sm font-semibold rounded-lg hover:bg-[#005a5d] disabled:opacity-50 transition">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-3">Per-Member Velocity</p>
            <div className="grid grid-cols-2 gap-3">
              <InputField label="Daily P2P send" value={editing.vl?.daily_p2p_send}
                onChange={(v) => setEditing((p) => ({ ...p, vl: { ...p.vl, daily_p2p_send: v } }))} />
              <InputField label="Single tx max" value={editing.vl?.single_tx_max}
                onChange={(v) => setEditing((p) => ({ ...p, vl: { ...p.vl, single_tx_max: v } }))} />
              <InputField label="Weekly cumulative" value={editing.vl?.weekly_cumulative}
                onChange={(v) => setEditing((p) => ({ ...p, vl: { ...p.vl, weekly_cumulative: v } }))} />
              <InputField label="Monthly cumulative" value={editing.vl?.monthly_cumulative}
                onChange={(v) => setEditing((p) => ({ ...p, vl: { ...p.vl, monthly_cumulative: v } }))} />
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-3">Per-Rail Daily Cap</p>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(editing.caps || {}).map(([rail, v]) => (
                <InputField key={rail} label={rail.replace("_", " ")} value={v}
                  onChange={(nv) => setEditing((p) => ({ ...p, caps: { ...p.caps, [rail]: nv } }))} />
              ))}
            </div>
          </div>
        </div>

        {/* 3b cool-off */}
        <div className="mt-6 pt-6 border-t border-slate-100">
          <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-3">Cool-off Periods</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <InputField label="Hours to lock new payee additions after failed login from new device"
              value={editing.co?.new_payee_lock_hours_after_login_failure} prefix=""
              onChange={(v) => setEditing((p) => ({ ...p, co: { ...p.co, new_payee_lock_hours_after_login_failure: v } }))} />
            <InputField label="Minutes to lock account after 3 failed PIN attempts"
              value={editing.co?.account_lock_minutes_after_3_failed_pin} prefix=""
              onChange={(v) => setEditing((p) => ({ ...p, co: { ...p.co, account_lock_minutes_after_3_failed_pin: v } }))} />
          </div>
        </div>

        <p className="text-xs text-slate-400 mt-4">
          Last updated: {policy.updated_at ? new Date(policy.updated_at).toLocaleString() : "—"}
          {policy.updated_by ? ` by ${policy.updated_by}` : ""}
        </p>
      </div>

      {/* 3c trusted devices */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-lg font-bold text-slate-900 mb-1">Trusted Device Registry</h3>
        <p className="text-xs text-slate-500 mb-4">{devices.length} active device(s)</p>
        <table className="w-full text-sm" data-testid="rails-devices-table">
          <thead>
            <tr className="text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
              <th className="text-left py-2">Member</th>
              <th className="text-left py-2">Device fingerprint</th>
              <th className="text-left py-2">Registered</th>
              <th className="text-left py-2">Last used</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {devices.map((d) => (
              <tr key={d.id} className="border-b border-slate-100" data-testid={`rails-device-row-${d.id}`}>
                <td className="py-3 font-medium text-slate-900">{d.member_name}</td>
                <td className="py-3 font-mono text-xs text-slate-600">{d.device_fingerprint}</td>
                <td className="py-3 text-xs text-slate-500">{fmtRel(d.registered_date)}</td>
                <td className="py-3 text-xs text-slate-500">{fmtRel(d.last_used)}</td>
                <td className="py-3 text-right">
                  <button onClick={() => revokeDevice(d.id)} data-testid={`rails-device-revoke-${d.id}`}
                    className="text-rose-600 hover:text-rose-800 text-xs font-semibold flex items-center gap-1 ml-auto">
                    <Trash2 className="w-3.5 h-3.5" /> Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 3d group rules */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Dynamic Group Rules</h3>
            <p className="text-xs text-slate-500">Conditional limits applied to member cohorts ({rules.length}/5)</p>
          </div>
          {rules.length < 5 && (
            <button onClick={() => setShowRuleForm(true)} data-testid="rails-rule-add-btn"
              className="px-3 py-2 bg-slate-900 text-white text-xs font-semibold rounded-lg hover:bg-slate-800 transition flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5" /> Add rule
            </button>
          )}
        </div>

        {showRuleForm && (
          <div className="bg-slate-50 rounded-lg border border-slate-200 p-4 mb-4 space-y-3" data-testid="rails-rule-form">
            <input type="text" placeholder="Rule name (e.g. New member protection)"
              value={newRule.name} onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-sm" data-testid="rails-rule-name-input" />
            <div className="grid grid-cols-4 gap-2 text-sm">
              <select value={newRule.attribute} onChange={(e) => setNewRule({ ...newRule, attribute: e.target.value })}
                className="px-2 py-2 bg-white border border-slate-200 rounded-md text-xs" data-testid="rails-rule-attribute">
                <option value="member_age_days">Member age (days)</option>
                <option value="kyc_tier">KYC tier</option>
                <option value="wallet_balance">Wallet balance</option>
              </select>
              <select value={newRule.operator} onChange={(e) => setNewRule({ ...newRule, operator: e.target.value })}
                className="px-2 py-2 bg-white border border-slate-200 rounded-md text-xs">
                <option value="<">{"<"}</option>
                <option value=">">{">"}</option>
                <option value="==">{"=="}</option>
              </select>
              <input type="number" value={newRule.value} onChange={(e) => setNewRule({ ...newRule, value: Number(e.target.value) })}
                className="px-2 py-2 bg-white border border-slate-200 rounded-md text-xs" />
              <select value={newRule.limit_type} onChange={(e) => setNewRule({ ...newRule, limit_type: e.target.value })}
                className="px-2 py-2 bg-white border border-slate-200 rounded-md text-xs">
                <option value="single_tx_max">Single tx max</option>
                <option value="daily_send">Daily send max</option>
              </select>
            </div>
            <div className="flex items-center gap-3">
              <label className="text-xs text-slate-600">Limit amount:</label>
              <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-md px-2 py-1.5">
                <span className="text-slate-400">$</span>
                <input type="number" value={newRule.limit_amount}
                  onChange={(e) => setNewRule({ ...newRule, limit_amount: Number(e.target.value) })}
                  className="w-24 outline-none text-sm font-mono" />
              </div>
              <button onClick={addRule} disabled={!newRule.name} data-testid="rails-rule-save-btn"
                className="ml-auto px-4 py-1.5 bg-[#007377] text-white text-xs font-semibold rounded-md disabled:opacity-50">
                Save rule
              </button>
              <button onClick={() => setShowRuleForm(false)} className="px-3 py-1.5 text-slate-500 text-xs">Cancel</button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {rules.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">No rules configured</p>
          ) : rules.map((r) => {
            const cond = r.condition || { attribute: r.attribute, operator: r.operator, value: r.value };
            const act = r.action || { type: r.limit_type, amount: r.limit_amount };
            const actLabel = act.type === "flag_for_review" ? "flag for compliance review"
              : act.type === "require_approval" ? "require manual approval"
              : act.type === "block" ? "block"
              : `set ${act.type} = ${fmtUSD(act.amount)}`;
            return (
              <div key={r.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-3" data-testid={`rails-rule-row-${r.id}`}>
                <div>
                  <p className="font-semibold text-slate-900 text-sm">{r.name}</p>
                  <p className="text-xs text-slate-500">
                    IF <span className="font-mono">{cond.attribute} {cond.operator} {cond.value}</span> → {actLabel}
                  </p>
                </div>
                <button onClick={() => deleteRule(r.id)} data-testid={`rails-rule-delete-${r.id}`}
                  className="text-rose-600 hover:text-rose-800 text-xs font-semibold flex items-center gap-1">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ── SECTION 4 — RFI/RFP queue ──

const RFISection = ({ toast }) => {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [responseAction, setResponseAction] = useState("approve");
  const [responseNote, setResponseNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => axios.get(`${API}/rfi`).then((r) => setItems(r.data.items)), []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/rfi/${selected.id}/respond`, { action: responseAction, note: responseNote });
      toast(`${selected.type} ${responseAction}d`);
      setSelected(null);
      setResponseNote("");
      load();
    } catch (e) {
      toast(e.response?.data?.detail || "Failed", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const statusColor = {
    pending: "bg-amber-100 text-amber-700",
    responded: "bg-emerald-100 text-emerald-700",
    expired: "bg-slate-200 text-slate-500",
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="rails-rfi-section">
      <div className="mb-4">
        <h3 className="text-lg font-bold text-slate-900">RFI / RFP Queue</h3>
        <p className="text-xs text-slate-500">Requests for Information & Requests for Payment · {items.length} item(s)</p>
      </div>

      <table className="w-full text-sm" data-testid="rails-rfi-table">
        <thead>
          <tr className="text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
            <th className="text-left py-2">Type</th>
            <th className="text-left py-2">From</th>
            <th className="text-left py-2">Member affected</th>
            <th className="text-right py-2">Amount</th>
            <th className="text-left py-2">Reason</th>
            <th className="text-left py-2">Received</th>
            <th className="text-center py-2">Status</th>
            <th className="py-2"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id} className="border-b border-slate-100" data-testid={`rails-rfi-row-${it.id}`}>
              <td className="py-3">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${it.type === "RFI" ? "bg-sky-100 text-sky-700" : "bg-violet-100 text-violet-700"}`}>{it.type}</span>
                <span className="ml-2 text-xs text-slate-500">{it.rail}</span>
              </td>
              <td className="py-3 text-xs text-slate-700 max-w-[160px] truncate">{it.from_party}</td>
              <td className="py-3 text-xs text-slate-700">{it.member_affected || "—"}</td>
              <td className="py-3 text-right font-mono text-slate-900">{fmtUSD(it.amount)}</td>
              <td className="py-3 text-xs text-slate-600 max-w-[240px] truncate">{it.reason}</td>
              <td className="py-3 text-xs text-slate-500">{fmtRel(it.received_at)}</td>
              <td className="py-3 text-center">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${statusColor[it.status]}`}>{it.status}</span>
              </td>
              <td className="py-3 text-right">
                <button onClick={() => setSelected(it)} disabled={it.status === "expired"} data-testid={`rails-rfi-review-${it.id}`}
                  className="text-[#007377] hover:text-[#005a5d] text-xs font-semibold disabled:text-slate-300">
                  Review
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {selected && (
        <div className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-xl max-w-lg w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="rails-rfi-modal">
            <div className="flex items-start justify-between">
              <div>
                <h4 className="text-lg font-bold text-slate-900">{selected.type} from {selected.from_party}</h4>
                <p className="text-xs text-slate-500">{selected.rail} · {fmtRel(selected.received_at)}</p>
              </div>
              <button onClick={() => setSelected(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Amount</span><span className="font-mono font-semibold">{fmtUSD(selected.amount)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Reason</span><span className="text-right">{selected.reason}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Tx reference</span><span className="font-mono text-xs">{selected.tx_reference}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Deadline</span><span>{new Date(selected.deadline_at).toLocaleString()}</span></div>
            </div>
            {selected.status === "pending" && (
              <>
                <div className="flex gap-2" data-testid="rails-rfi-action-buttons">
                  {[
                    { id: "approve", label: "Approve", color: "bg-emerald-600 hover:bg-emerald-700" },
                    { id: "decline", label: "Decline", color: "bg-rose-600 hover:bg-rose-700" },
                    { id: "respond", label: "Respond w/ Info", color: "bg-slate-700 hover:bg-slate-800" },
                  ].map((a) => (
                    <button key={a.id} onClick={() => setResponseAction(a.id)} data-testid={`rails-rfi-action-${a.id}`}
                      className={`flex-1 py-2 rounded-lg text-white text-sm font-semibold transition ${a.color} ${responseAction === a.id ? "ring-2 ring-offset-2 ring-slate-400" : ""}`}>
                      {a.label}
                    </button>
                  ))}
                </div>
                <textarea value={responseNote} onChange={(e) => setResponseNote(e.target.value)}
                  placeholder="Note (optional)" rows={2} data-testid="rails-rfi-note"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                <button onClick={submit} disabled={submitting} data-testid="rails-rfi-submit-btn"
                  className="w-full py-2.5 bg-[#007377] text-white font-semibold rounded-lg hover:bg-[#005a5d] disabled:opacity-50">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Submit response"}
                </button>
              </>
            )}
            {selected.status !== "pending" && (
              <p className="text-sm text-slate-500 italic">This request has already been {selected.status}.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── SECTION 5 — API Gateway ──

const APIGatewaySection = ({ toast }) => {
  const brand = useBranding();
  const [keys, setKeys] = useState([]);
  const [hooks, setHooks] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newKey, setNewKey] = useState({
    name: "", scope: "read",
    allowed_rails: ["fednow"], rate_limit_rpm: 600, expires_choice: "1y",
  });
  const [newRawKey, setNewRawKey] = useState(null);
  const [newHook, setNewHook] = useState({ url: "", events: "transaction.completed,transaction.failed" });

  const load = useCallback(async () => {
    const [k, h] = await Promise.all([axios.get(`${API}/api-keys`), axios.get(`${API}/webhooks`)]);
    setKeys(k.data.keys);
    setHooks(h.data.webhooks);
  }, []);
  useEffect(() => { load(); }, [load]);

  const createKey = async () => {
    try {
      const res = await axios.post(`${API}/api-keys`, newKey);
      setNewRawKey(res.data.raw_key);
      setNewKey({ name: "", scope: "read", allowed_rails: ["fednow"], rate_limit_rpm: 600, expires_choice: "1y" });
      setShowCreateForm(false);
      toast("API key created");
      load();
    } catch (e) { toast(e.response?.data?.detail || "Failed", "error"); }
  };

  const rotateKey = async (id) => {
    try {
      const res = await axios.patch(`${API}/api-keys/${id}/rotate`);
      setNewRawKey(res.data.raw_key);
      toast("Key rotated");
      load();
    } catch (e) { toast(e.response?.data?.detail || "Failed", "error"); }
  };

  const revokeKey = async (id) => {
    await axios.delete(`${API}/api-keys/${id}`);
    toast("Key revoked");
    load();
  };

  const addWebhook = async () => {
    try {
      await axios.post(`${API}/webhooks`, {
        url: newHook.url,
        events: newHook.events.split(",").map((s) => s.trim()).filter(Boolean),
      });
      setNewHook({ url: "", events: "transaction.completed,transaction.failed" });
      toast("Webhook added");
      load();
    } catch (_e) { toast("Failed", "error"); }
  };

  const toggleAllowedRail = (rail) => {
    setNewKey((p) => ({
      ...p,
      allowed_rails: p.allowed_rails.includes(rail)
        ? p.allowed_rails.filter((r) => r !== rail)
        : [...p.allowed_rails, rail],
    }));
  };

  const ALL_RAILS = [
    { id: "fednow", label: "FedNow" },
    { id: "rtp", label: "RTP" },
    { id: "stablecoin", label: "Stablecoin" },
    { id: "tokenized_deposit", label: "Tokenized Deposit" },
  ];

  return (
    <div className="space-y-5" data-testid="rails-api-section">
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-1">
          <div>
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2"><Key className="w-5 h-5 text-[#007377]" /> Open API Gateway</h3>
            <p className="text-xs text-slate-500 mt-0.5">Connect your loan origination, payroll, and treasury systems directly to {brand.name} rails.</p>
          </div>
          <button onClick={() => setShowCreateForm(true)} data-testid="rails-apikey-create-btn"
            className="px-3 py-2 bg-[#007377] text-white text-xs font-semibold rounded-lg hover:bg-[#005a5d] transition flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> Generate New API Key
          </button>
        </div>

        {showCreateForm && (
          <div className="bg-slate-50 rounded-lg border border-slate-200 p-4 my-4 space-y-3" data-testid="rails-apikey-form">
            <input type="text" placeholder="Key name (e.g. Loan Origination System)" value={newKey.name}
              onChange={(e) => setNewKey({ ...newKey, name: e.target.value })}
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-sm"
              data-testid="rails-apikey-name-input" />
            <div className="grid grid-cols-3 gap-3">
              <select value={newKey.scope} onChange={(e) => setNewKey({ ...newKey, scope: e.target.value })}
                className="px-3 py-2 bg-white border border-slate-200 rounded-md text-sm" data-testid="rails-apikey-scope">
                <option value="read">Scope: Read</option>
                <option value="write">Scope: Write</option>
                <option value="admin">Scope: Admin</option>
              </select>
              <input type="number" value={newKey.rate_limit_rpm}
                onChange={(e) => setNewKey({ ...newKey, rate_limit_rpm: Number(e.target.value) })}
                placeholder="Rate limit (requests/min)"
                className="px-3 py-2 bg-white border border-slate-200 rounded-md text-sm"
                data-testid="rails-apikey-ratelimit" />
              <select value={newKey.expires_choice} onChange={(e) => setNewKey({ ...newKey, expires_choice: e.target.value })}
                className="px-3 py-2 bg-white border border-slate-200 rounded-md text-sm" data-testid="rails-apikey-expires">
                <option value="30d">Expires in 30 days</option>
                <option value="90d">Expires in 90 days</option>
                <option value="1y">Expires in 1 year</option>
                <option value="never">Never expires</option>
              </select>
            </div>
            <div>
              <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-2">Allowed rails</p>
              <div className="flex flex-wrap gap-2" data-testid="rails-apikey-rails-multi">
                {ALL_RAILS.map((r) => {
                  const active = newKey.allowed_rails.includes(r.id);
                  return (
                    <button key={r.id} type="button" onClick={() => toggleAllowedRail(r.id)}
                      data-testid={`rails-apikey-rail-${r.id}`}
                      className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition ${
                        active
                          ? "bg-[#007377] text-white border-[#007377]"
                          : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                      }`}>
                      {r.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={createKey} disabled={!newKey.name || newKey.allowed_rails.length === 0} data-testid="rails-apikey-save-btn"
                className="px-4 py-1.5 bg-[#007377] text-white text-xs font-semibold rounded-md disabled:opacity-50">Generate</button>
              <button onClick={() => setShowCreateForm(false)} className="px-3 py-1.5 text-slate-500 text-xs">Cancel</button>
            </div>
          </div>
        )}

        {newRawKey && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4" data-testid="rails-apikey-display">
            <p className="text-xs font-semibold text-amber-800 mb-1">⚠ Save this key now — it won't be shown again</p>
            <code className="block bg-white px-3 py-2 rounded font-mono text-xs text-slate-900 break-all">{newRawKey}</code>
            <button onClick={() => setNewRawKey(null)} className="mt-2 text-xs font-semibold text-amber-800">I've saved it →</button>
          </div>
        )}

        <table className="w-full text-sm" data-testid="rails-apikey-table">
          <thead>
            <tr className="text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
              <th className="text-left py-2">Name</th>
              <th className="text-left py-2">Key ID</th>
              <th className="text-left py-2">Scope</th>
              <th className="text-left py-2">Rails</th>
              <th className="text-right py-2">Rate limit</th>
              <th className="text-left py-2">Expires</th>
              <th className="text-left py-2">Last used</th>
              <th className="text-center py-2">Status</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.id} className="border-b border-slate-100" data-testid={`rails-apikey-row-${k.id}`}>
                <td className="py-3 font-medium text-slate-900">{k.name}</td>
                <td className="py-3 font-mono text-xs text-slate-500">{k.key_id_masked}</td>
                <td className="py-3"><span className="text-[10px] font-bold px-2 py-0.5 rounded uppercase bg-slate-100 text-slate-700">{k.scope}</span></td>
                <td className="py-3 text-xs text-slate-600">{(k.allowed_rails || []).map((r) => r.replace("_", " ")).join(", ") || "—"}</td>
                <td className="py-3 text-right text-xs text-slate-700 font-mono">{k.rate_limit_rpm ? `${k.rate_limit_rpm}/min` : "—"}</td>
                <td className="py-3 text-xs text-slate-500">{k.expires_at ? new Date(k.expires_at).toLocaleDateString() : "Never"}</td>
                <td className="py-3 text-xs text-slate-500">{fmtRel(k.last_used)}</td>
                <td className="py-3 text-center">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${k.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{k.status}</span>
                </td>
                <td className="py-3 text-right">
                  {k.status === "active" && (
                    <div className="flex items-center justify-end gap-3">
                      <button onClick={() => rotateKey(k.id)} data-testid={`rails-apikey-rotate-${k.id}`}
                        className="text-[#007377] hover:text-[#005a5d] text-xs font-semibold flex items-center gap-1">
                        <RefreshCw className="w-3.5 h-3.5" /> Rotate
                      </button>
                      <button onClick={() => revokeKey(k.id)} data-testid={`rails-apikey-revoke-${k.id}`}
                        className="text-rose-600 hover:text-rose-800 text-xs font-semibold flex items-center gap-1">
                        <Trash2 className="w-3.5 h-3.5" /> Revoke
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* API docs snippet */}
        <div className="mt-6 pt-6 border-t border-slate-100">
          <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">API Documentation</p>
          <div className="bg-slate-900 text-slate-100 rounded-lg p-4 font-mono text-xs leading-relaxed overflow-x-auto" data-testid="rails-api-docs-snippet">
            <p className="text-slate-400">{`# Sample request — route a $250 P2P payment`}</p>
            <p>POST <span className="text-emerald-300">https://api.{brand.domain}/v2/route</span></p>
            <p>Authorization: Bearer <span className="text-amber-300">ngp_live_••••</span></p>
            <p>Content-Type: application/json</p>
            <pre className="mt-2 text-slate-200">{`{
  "amount": 250.00,
  "currency": "USD",
  "recipient_identifier": "+15555551234",
  "member_preference": "default"
}`}</pre>
          </div>
          <p className="text-xs text-slate-400 mt-2">
            Base URL: <span className="font-mono">https://api.{brand.domain}</span> · Full docs at <span className="font-mono">/docs</span>
          </p>
        </div>
      </div>

      {/* Webhooks */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2 mb-1"><Webhook className="w-5 h-5 text-[#007377]" /> Webhooks</h3>
        <p className="text-xs text-slate-500 mb-4">Bank-side endpoints to receive transaction & rail events</p>

        <div className="space-y-2 mb-4">
          {hooks.map((h) => (
            <div key={h.id} className="bg-slate-50 rounded-lg px-4 py-3 flex items-center justify-between" data-testid={`rails-webhook-row-${h.id}`}>
              <div>
                <p className="font-mono text-sm text-slate-900">{h.url}</p>
                <p className="text-xs text-slate-500 mt-0.5">{h.events.join(" · ")}</p>
              </div>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded uppercase bg-emerald-100 text-emerald-700">{h.active ? "active" : "paused"}</span>
            </div>
          ))}
        </div>

        <div className="flex gap-2" data-testid="rails-webhook-add-form">
          <input type="text" value={newHook.url} placeholder="https://your-system.com/webhook"
            onChange={(e) => setNewHook({ ...newHook, url: e.target.value })}
            className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-md text-sm font-mono" />
          <input type="text" value={newHook.events}
            onChange={(e) => setNewHook({ ...newHook, events: e.target.value })}
            className="w-72 px-3 py-2 bg-white border border-slate-200 rounded-md text-xs"
            placeholder="event1,event2" />
          <button onClick={addWebhook} disabled={!newHook.url} data-testid="rails-webhook-add-btn"
            className="px-3 py-2 bg-slate-900 text-white text-xs font-semibold rounded-md disabled:opacity-50">
            Add webhook
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main page ──

export const RailsOperationsTab = () => {
  const [toastState, setToastState] = useState({ msg: "", kind: "success" });
  const toast = (msg, kind = "success") => setToastState({ msg, kind });

  return (
    <div data-testid="admin-rails-operations">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Rails Operations</h2>
          <p className="text-sm text-slate-500">Unified operations console for FedNow, RTP, Stablecoin, and Tokenized Deposit rails</p>
        </div>
      </div>

      <div className="space-y-6">
        <StatusSection />
        <VolumeSection />
        <FraudControlsSection toast={toast} />
        <RFISection toast={toast} />
        <APIGatewaySection toast={toast} />
      </div>

      <Toast msg={toastState.msg} kind={toastState.kind} onClose={() => setToastState({ msg: "", kind: "success" })} />
    </div>
  );
};
